/* eslint-disable react/jsx-indent */
import React, { useCallback, useMemo } from 'react';
import { Text } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { toggleExpandedPath } from '../../../../redux/providerBuilder/jsonViewer';
import { getExpandedPaths } from '../../../../redux/providerBuilder/jsonViewer/selectors';

interface JsonViewerProps {
  data?: any;
  path?: string;
  onItemClick?: any;
  level?: number;
  searchQuery?: any;
}

const NormalJsonViewer: React.FC<JsonViewerProps> = ({
  data,
  path = '',
  onItemClick,
  level = 0,
  searchQuery = '',
}) => {
  const expandedPaths = useAppSelector(getExpandedPaths);
  const dispatch = useAppDispatch();

  const toggleExpand = useCallback(
    (path: string) => {
      dispatch(toggleExpandedPath(path));
    },
    [dispatch]
  );

  const isArray = useMemo(() => Array.isArray(data), [data]);
  const entries = useMemo(() => Object.entries(data || {}), [data]);
  const isExpanded = useMemo(() => expandedPaths[path] ?? true, [expandedPaths, path]);

  if (typeof data !== 'object' || data === null) {
    return (
      <div
        className="json-item font-roboto-mono-regular"
        style={{ paddingLeft: `${5}px` }}
        onClick={() => onItemClick(path)}
      >
        <span className="json-value medium-font font-roboto-mono-regular">
          {String(data)}
        </span>
      </div>
    );
  }

  return (
    <div className="json-container medium-font">
      <span style={{ paddingLeft: `${5}px` }}>
        {isArray ? '[' : '{'}
        {entries.length > 0 && (
          <span className="toggle-button medium-font" onClick={() => toggleExpand(path)}>
            {isExpanded ? <span>&#8854;</span> : <span>&#8853;</span>}
          </span>
        )}
      </span>
      {isExpanded &&
        entries.map(([key, value]: any[], index: number) => (
          <div
            key={path + key}
            className="json-item font-roboto-mono-regular medium-font"
          >
            {!isArray &&
              (String(value)?.includes('{{REQ_BODY_') ||
              String(value)?.includes('{{REQ_SECRET_') ? (
                <span
                  className="json-key highlight font-roboto-mono-regular medium-font"
                  style={{ lineHeight: 1.5 }}
                  onClick={() => onItemClick(path + (isArray ? `[${key}]` : `.${key}`))}
                >
                  {key}
                </span>
              ) : (
                <span
                  className="json-key font-roboto-mono-regular medium-font"
                  style={{ lineHeight: 1.5 }}
                  onClick={() => onItemClick(path + (isArray ? `[${key}]` : `.${key}`))}
                >
                  {key}
                </span>
              ))}
            {!isArray && ': '}
            {typeof value === 'object' && value !== null ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <NormalJsonViewer
                  data={value}
                  // path={`${path}${isArray ? `[${key}]` : `.${key}`}`}
                  path={`${path}.${key}`}
                  onItemClick={onItemClick}
                  level={level + 1}
                  searchQuery={searchQuery}
                />
              </div>
            ) : (
              <span
                className={`json-value ${
                  (String(value)?.includes('{{REQ_BODY_') ||
                    String(value)?.includes('{{REQ_SECRET_')) &&
                  'highlight medium-font'
                }`}
                onClick={() => onItemClick(path + (isArray ? `[${key}]` : `.${key}`))}
                style={{ marginLeft: '5px', cursor: 'pointer' }}
              >
                {String(value)?.includes('{{REQ_BODY') ? (
                  <Text
                    className="font-roboto medium-font"
                    style={{
                      fontWeight: 300,
                      lineHeight: 1.5,
                      display: 'inline-block',
                    }}
                  >
                    {String(value)}
                  </Text>
                ) : (
                  <>
                    {typeof value === 'string' && <>&quot;</>}
                    <Text
                      className="font-roboto medium-font"
                      style={{
                        fontWeight: 300,
                        lineHeight: 1.5,
                        display: 'inline-block',
                      }}
                    >
                      {String(value)}
                    </Text>
                    {typeof value === 'string' && <>&quot;</>}
                  </>
                )}

                {index < entries.length - 1 && ','}
              </span>
            )}
          </div>
        ))}
      <span className="medium-font" style={{ paddingLeft: `${5}px` }}>
        {isArray ? ']' : '}'}
      </span>
    </div>
  );
};

export default React.memo(NormalJsonViewer);
